import {
  Lead, DocP, DocH2, Callout, Card, Grid, Bento, Steps, Expandable, CodeBlock, PropTable, Pill,
} from "@/docs/components";
import {
  Lock, Layers, Clock, Waves, Send, Wallet, Cpu, ShieldCheck, KeyRound, Boxes,
  Zap, GitBranch, Terminal, Globe, FileCheck, Users,
} from "lucide-react";

/* Each page: slug, title, section, motif, description, keywords, headings, Content */

export const PAGES = [
  // ---------------- OVERVIEW ----------------
  {
    slug: "introduction",
    title: "Introduction",
    section: "Overview",
    motif: "interlock",
    eyebrow: "welock docs",
    description: "welock is a non custodial protocol for token and liquidity locking, vesting and distribution on Robinhood Chain.",
    keywords: ["overview", "what is welock", "protocol", "intro"],
    headings: [
      { id: "what-is-welock", label: "What is welock" },
      { id: "core-primitives", label: "Core primitives" },
      { id: "why-lock", label: "Why teams lock" },
    ],
    Content: () => (
      <div className="space-y-8">
        <Lead>
          welock is a non custodial protocol for locking tokens and liquidity, running vesting
          schedules, sending batch payouts and exposing all of it to agents through a clean API.
          Everything settles on Robinhood Chain.
        </Lead>
        <DocH2 id="what-is-welock">What is welock</DocH2>
        <DocP>
          Teams use welock to prove that supply is locked, to release tokens on a schedule and to
          distribute value at scale. Locks are verifiable, self custodied and cannot be moved before
          the unlock date. There is no admin key over your assets.
        </DocP>
        <Grid cols={3}>
          <Card icon={Lock} title="Non custodial">Only the beneficiary can claim after unlock. welock never holds keys.</Card>
          <Card icon={FileCheck} title="Verifiable">Every lock has a public proof with amount, unlock date and schedule.</Card>
          <Card icon={Zap} title="Low fee">Built for Robinhood Chain with ultra low fees and fast finality.</Card>
        </Grid>
        <DocH2 id="core-primitives">Core primitives</DocH2>
        <Bento>
          <Card icon={Lock} title="Token Locker" span="lg:col-span-2">Lock ERC-20 supply with a cliff or linear release.</Card>
          <Card icon={Layers} title="LP Locker">Lock Uniswap V3 and V4 positions.</Card>
          <Card icon={Clock} title="Vesting">Stakeholder schedules with cliffs.</Card>
          <Card icon={Send} title="Multisender" span="lg:col-span-2">Batch transfers to thousands of wallets in one run.</Card>
        </Bento>
        <DocH2 id="why-lock">Why teams lock</DocH2>
        <Callout type="tip" title="Trust is a feature">
          Locking supply and liquidity is the clearest signal a team can send to holders. It removes
          the ability to rug and makes intentions legible on chain.
        </Callout>
      </div>
    ),
  },
  {
    slug: "getting-started",
    title: "Getting Started",
    section: "Overview",
    motif: "rocket",
    eyebrow: "quickstart",
    description: "Connect a wallet and create your first lock in a few minutes.",
    keywords: ["getting started", "connect wallet", "quickstart", "privy"],
    headings: [
      { id: "connect", label: "Connect a wallet" },
      { id: "first-lock", label: "Create your first lock" },
      { id: "next", label: "Where to go next" },
    ],
    Content: () => (
      <div className="space-y-8">
        <Lead>Getting started takes three steps: connect, configure, confirm. No sign up, wallet only.</Lead>
        <DocH2 id="connect">Connect a wallet</DocH2>
        <DocP>
          welock uses Privy for wallet authentication. You can connect an external wallet like
          MetaMask, or continue with email to get an embedded wallet.
        </DocP>
        <Steps
          items={[
            { title: "Open the app", body: "Click Launch App from the landing page or top navigation." },
            { title: "Choose a method", body: "Connect an external wallet or continue with email for an embedded wallet." },
            { title: "Land on the dashboard", body: "The overview loads with your positions, the unlock schedule and recent activity." },
          ]}
        />
        <DocH2 id="first-lock">Create your first lock</DocH2>
        <DocP>Open Token Locker, fill the form, review, then confirm.</DocP>
        <CodeBlock lang="text" code={`Token contract   0x... (the ERC-20 you want to lock)
Amount           1,000,000
Recipient        0x... (who can claim at unlock)
Unlock date      a future date and time
Schedule         cliff or linear`} />
        <Callout type="warning" title="Double check the unlock date">
          A lock cannot be released before its unlock date. Confirm the date and recipient before you sign.
        </Callout>
        <DocH2 id="next">Where to go next</DocH2>
        <Grid cols={3}>
          <Card icon={Lock} title="Token Locker">Lock ERC-20 supply.</Card>
          <Card icon={Clock} title="Vesting">Set up stakeholder schedules.</Card>
          <Card icon={Cpu} title="Agents API">Automate locking from code.</Card>
        </Grid>
      </div>
    ),
  },
  {
    slug: "robinhood-chain",
    title: "Robinhood Chain",
    section: "Overview",
    motif: "robinhood",
    eyebrow: "the network",
    description: "welock is built natively for Robinhood Chain with full EVM compatibility.",
    keywords: ["robinhood chain", "network", "evm", "mainnet", "testnet"],
    headings: [
      { id: "overview", label: "Network overview" },
      { id: "params", label: "Network parameters" },
      { id: "assets", label: "Supported assets" },
    ],
    Content: () => (
      <div className="space-y-8">
        <Lead>welock treats Robinhood Chain as its single target network. One network, zero bridge risk.</Lead>
        <DocH2 id="overview">Network overview</DocH2>
        <DocP>
          Robinhood Chain is EVM compatible with ultra low fees and fast finality. welock integrates
          directly with ecosystem assets and NFT liquidity positions.
        </DocP>
        <Grid cols={3}>
          <Card icon={Globe} title="EVM compatible">Standard tooling, wallets and contracts work out of the box.</Card>
          <Card icon={Zap} title="Low fees">Locking, vesting and multisends stay cheap even at scale.</Card>
          <Card icon={Boxes} title="Native assets">Direct support for ecosystem tokens and LP NFTs.</Card>
        </Grid>
        <DocH2 id="params">Network parameters</DocH2>
        <PropTable
          rows={[
            { field: "Network", type: "string", desc: "Robinhood Chain Mainnet" },
            { field: "Type", type: "EVM", desc: "Full Ethereum Virtual Machine compatibility" },
            { field: "Finality", type: "fast", desc: "Sub second confirmations for most actions" },
            { field: "Testnet", type: "available", desc: "Robinhood Chain Testnet for integration" },
          ]}
        />
        <DocH2 id="assets">Supported assets</DocH2>
        <Callout type="info" title="ERC-20 and LP NFTs">
          welock supports standard ERC-20 tokens and Uniswap V3 and V4 liquidity NFT positions.
        </Callout>
      </div>
    ),
  },

  // ---------------- PRODUCTS ----------------
  {
    slug: "token-locker",
    title: "Token Locker",
    section: "Products",
    motif: "lock",
    eyebrow: "product",
    description: "Lock ERC-20 tokens with a cliff or linear release schedule.",
    keywords: ["token locker", "lock tokens", "erc20", "cliff", "linear"],
    headings: [
      { id: "how-it-works", label: "How it works" },
      { id: "fields", label: "Lock fields" },
      { id: "schedules", label: "Release schedules" },
      { id: "faq", label: "Common questions" },
    ],
    Content: () => (
      <div className="space-y-8">
        <Lead>The Token Locker secures ERC-20 supply until a chosen unlock date, with an optional linear release.</Lead>
        <DocH2 id="how-it-works">How it works</DocH2>
        <Steps
          items={[
            { title: "Approve", body: "Grant the locker permission to move the exact amount you are locking." },
            { title: "Lock", body: "Tokens move into the lock. A proof is created with the amount, recipient and unlock date." },
            { title: "Claim", body: "After unlock, the recipient claims. With a linear schedule, claims unlock gradually." },
          ]}
        />
        <DocH2 id="fields">Lock fields</DocH2>
        <PropTable
          rows={[
            { field: "token_address", type: "address", desc: "The ERC-20 contract to lock" },
            { field: "amount", type: "number", desc: "Quantity of tokens to lock" },
            { field: "recipient", type: "address", desc: "The address that can claim at unlock" },
            { field: "unlock_date", type: "datetime", desc: "When tokens become claimable" },
            { field: "schedule", type: "cliff | linear", desc: "Release style" },
          ]}
        />
        <DocH2 id="schedules">Release schedules</DocH2>
        <Grid cols={2}>
          <Card icon={Lock} title="Cliff">Everything unlocks at once on the unlock date.</Card>
          <Card icon={Clock} title="Linear">Tokens unlock continuously up to the unlock date.</Card>
        </Grid>
        <DocH2 id="faq">Common questions</DocH2>
        <div className="space-y-3">
          <Expandable title="Can I extend a lock?">Yes. You can extend the unlock date to a later time. You cannot shorten it.</Expandable>
          <Expandable title="Can I lock to a different recipient?">Yes. The recipient is set at creation and is the only address that can claim.</Expandable>
          <Expandable title="What happens at unlock?">The recipient claims from the dashboard or through the API. Nothing moves automatically.</Expandable>
        </div>
      </div>
    ),
  },
  {
    slug: "lp-locker",
    title: "LP Locker",
    section: "Products",
    motif: "interlock",
    eyebrow: "product",
    description: "Lock Uniswap V3 and V4 liquidity NFT positions on Robinhood Chain.",
    keywords: ["lp locker", "liquidity", "uniswap", "nft position"],
    headings: [
      { id: "overview", label: "Overview" },
      { id: "positions", label: "Locking positions" },
      { id: "fees", label: "Fee collection" },
    ],
    Content: () => (
      <div className="space-y-8">
        <Lead>The LP Locker secures Uniswap V3 and V4 liquidity NFT positions so holders can trust that liquidity stays put.</Lead>
        <DocH2 id="overview">Overview</DocH2>
        <DocP>Instead of a token balance, an LP lock holds a liquidity NFT. The position stays live and continues to earn.</DocP>
        <Callout type="tip" title="Liquidity stays productive">Locked positions keep providing liquidity. Only withdrawal is time gated.</Callout>
        <DocH2 id="positions">Locking positions</DocH2>
        <Steps
          items={[
            { title: "Select position", body: "Choose the V3 or V4 NFT position to lock." },
            { title: "Set unlock", body: "Pick the unlock date for the position." },
            { title: "Confirm", body: "The position moves into the lock with a public proof." },
          ]}
        />
        <DocH2 id="fees">Fee collection</DocH2>
        <Grid cols={2}>
          <Card icon={Layers} title="Position preserved">The full range and liquidity of your position is preserved.</Card>
          <Card icon={Zap} title="Fees continue">Trading fees keep accruing while the position is locked.</Card>
        </Grid>
      </div>
    ),
  },
  {
    slug: "vesting",
    title: "Vesting",
    section: "Products",
    motif: "bars",
    eyebrow: "product",
    description: "Linear or cliff based token distributions for teams and investors.",
    keywords: ["vesting", "cliff", "linear", "team", "investors", "schedule"],
    headings: [
      { id: "overview", label: "Overview" },
      { id: "cliff-vs-linear", label: "Cliff vs linear" },
      { id: "example", label: "Example schedule" },
    ],
    Content: () => (
      <div className="space-y-8">
        <Lead>Vesting releases tokens to a beneficiary gradually, with an optional cliff before releases begin.</Lead>
        <DocH2 id="overview">Overview</DocH2>
        <DocP>Use vesting for team allocations, investor rounds and contributor grants. Each schedule is a lock with a release curve.</DocP>
        <DocH2 id="cliff-vs-linear">Cliff vs linear</DocH2>
        <Grid cols={2}>
          <Card icon={Clock} title="Cliff">No tokens release until the cliff date, then the schedule begins.</Card>
          <Card icon={GitBranch} title="Linear">Tokens release continuously across the vesting window.</Card>
        </Grid>
        <DocH2 id="example">Example schedule</DocH2>
        <CodeBlock lang="text" code={`Total        4,000,000 WLK
Cliff        6 months
Duration     24 months linear
Beneficiary  0x...team`} />
        <Callout type="info" title="Claim as it vests">The beneficiary can claim the vested portion at any time after the cliff.</Callout>
      </div>
    ),
  },
  {
    slug: "lp-vesting",
    title: "LP Vesting",
    section: "Products",
    motif: "waves",
    eyebrow: "product",
    description: "Automated liquidity unlock schedules synced to protocol milestones.",
    keywords: ["lp vesting", "liquidity vesting", "milestones", "schedule"],
    headings: [
      { id: "overview", label: "Overview" },
      { id: "milestones", label: "Milestone unlocks" },
    ],
    Content: () => (
      <div className="space-y-8">
        <Lead>LP Vesting releases locked liquidity on a schedule, so protocols can grow float in a predictable way.</Lead>
        <DocH2 id="overview">Overview</DocH2>
        <DocP>Rather than unlocking all liquidity at once, LP Vesting steps liquidity out over time or at set milestones.</DocP>
        <DocH2 id="milestones">Milestone unlocks</DocH2>
        <Steps
          items={[
            { title: "Define steps", body: "Set the portions and dates for each unlock step." },
            { title: "Lock liquidity", body: "The LP position moves into the vesting lock." },
            { title: "Release", body: "Each step becomes claimable at its date." },
          ]}
        />
        <Callout type="tip" title="Predictable float">Stepped releases keep the market informed and reduce shock to price.</Callout>
      </div>
    ),
  },
  {
    slug: "multisender",
    title: "Multisender",
    section: "Products",
    motif: "distribute",
    eyebrow: "product",
    description: "Batch transfers to thousands of wallets in a single gas efficient run.",
    keywords: ["multisender", "airdrop", "batch", "payout", "csv"],
    headings: [
      { id: "overview", label: "Overview" },
      { id: "format", label: "Input format" },
      { id: "flow", label: "Send flow" },
    ],
    Content: () => (
      <div className="space-y-8">
        <Lead>Multisender sends a token to many recipients at once. Paste a list, preview, then send.</Lead>
        <DocH2 id="overview">Overview</DocH2>
        <DocP>Use it for airdrops, payroll and reward payouts. The preview validates addresses and estimates gas before you send.</DocP>
        <DocH2 id="format">Input format</DocH2>
        <DocP>One recipient per line, address and amount separated by a comma or space.</DocP>
        <CodeBlock lang="text" code={`0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b, 1000
0x2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c, 2500
0x3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d, 750.5`} />
        <DocH2 id="flow">Send flow</DocH2>
        <Steps
          items={[
            { title: "Parse", body: "welock parses each line and flags invalid addresses." },
            { title: "Preview", body: "See totals, valid and invalid counts and estimated gas." },
            { title: "Send", body: "Confirm to send the batch in one run." },
          ]}
        />
        <Callout type="warning" title="Check invalid rows">Invalid addresses are excluded from the send. Fix them and re parse to include them.</Callout>
      </div>
    ),
  },
  {
    slug: "portfolio",
    title: "Portfolio",
    section: "Products",
    motif: "interlock",
    eyebrow: "product",
    description: "A single view of every lock, vesting schedule and LP position you own.",
    keywords: ["portfolio", "positions", "claimable", "active", "unlocked"],
    headings: [
      { id: "overview", label: "Overview" },
      { id: "statuses", label: "Position statuses" },
    ],
    Content: () => (
      <div className="space-y-8">
        <Lead>The Portfolio shows all positions owned by your connected wallet, filterable by status.</Lead>
        <DocH2 id="overview">Overview</DocH2>
        <DocP>Track total value, active value, claimable value and position count at a glance.</DocP>
        <DocH2 id="statuses">Position statuses</DocH2>
        <Grid cols={3}>
          <Card icon={Lock} title="Active">Locked and not yet claimable.</Card>
          <Card icon={Wallet} title="Claimable">Unlocked and ready to claim.</Card>
          <Card icon={FileCheck} title="Unlocked">Already released.</Card>
        </Grid>
      </div>
    ),
  },

  // ---------------- DEVELOPERS ----------------
  {
    slug: "agents-api",
    title: "Agents API",
    section: "Developers",
    motif: "api",
    eyebrow: "developers",
    description: "Programmatic locking, vesting and multisends for agents and bots.",
    keywords: ["api", "agents", "keys", "endpoints", "webhooks", "developers"],
    headings: [
      { id: "auth", label: "Authentication" },
      { id: "endpoints", label: "Endpoints" },
      { id: "example", label: "Example request" },
      { id: "webhooks", label: "Webhooks" },
    ],
    Content: () => (
      <div className="space-y-8">
        <Lead>The Agents API lets code do everything the dashboard does. Issue a key, call an endpoint, ship.</Lead>
        <DocH2 id="auth">Authentication</DocH2>
        <DocP>Generate an API key in the Agents panel. Send it as a bearer token on every request.</DocP>
        <CodeBlock lang="bash" code={`curl https://api.welock.xyz/v1/locks \\
  -H "Authorization: Bearer wlk_live_xxx"`} />
        <Callout type="danger" title="Keep keys secret">Treat keys like passwords. Revoke and reissue immediately if a key leaks.</Callout>
        <DocH2 id="endpoints">Endpoints</DocH2>
        <div className="space-y-2">
          {[
            ["POST", "/v1/locks", "Create a token or LP lock"],
            ["GET", "/v1/locks/{id}", "Fetch a lock status and schedule"],
            ["GET", "/v1/locks", "List locks for an address"],
            ["POST", "/v1/vesting", "Create a vesting schedule"],
            ["POST", "/v1/multisend", "Batch transfer to many recipients"],
            ["POST", "/v1/webhooks", "Register unlock and claim alerts"],
          ].map(([m, p, d]) => (
            <div key={`${m}-${p}`} className="flex items-center gap-4 border border-welock-border bg-welock-surface px-4 py-3">
              <Pill tone={m === "GET" ? "get" : "post"}>{m}</Pill>
              <span className="font-mono text-[13px] text-welock-textPrimary">{p}</span>
              <span className="ml-auto hidden text-sm font-light text-welock-textMuted sm:block">{d}</span>
            </div>
          ))}
        </div>
        <DocH2 id="example">Example request</DocH2>
        <CodeBlock lang="json" code={`POST /v1/locks
{
  "token_address": "0x...",
  "amount": 1000000,
  "recipient": "0x...",
  "unlock_date": "2027-01-01T00:00:00Z",
  "schedule": "linear"
}`} />
        <DocH2 id="webhooks">Webhooks</DocH2>
        <Grid cols={2}>
          <Card icon={Terminal} title="unlock.ready">Fires when a lock becomes claimable.</Card>
          <Card icon={KeyRound} title="lock.created">Fires when a new lock is created.</Card>
        </Grid>
      </div>
    ),
  },

  // ---------------- RESOURCES ----------------
  {
    slug: "security",
    title: "Security",
    section: "Resources",
    motif: "shield",
    eyebrow: "resources",
    description: "How welock keeps assets safe and self custodied.",
    keywords: ["security", "audit", "non custodial", "safety"],
    headings: [
      { id: "model", label: "Security model" },
      { id: "practices", label: "Best practices" },
    ],
    Content: () => (
      <div className="space-y-8">
        <Lead>welock is non custodial by design. You keep control of your assets at every step.</Lead>
        <DocH2 id="model">Security model</DocH2>
        <Grid cols={3}>
          <Card icon={ShieldCheck} title="Self custody">No admin key can move your locked assets.</Card>
          <Card icon={FileCheck} title="Verifiable">Every lock is publicly provable on chain.</Card>
          <Card icon={Users} title="Beneficiary only">Only the recipient can claim after unlock.</Card>
        </Grid>
        <DocH2 id="practices">Best practices</DocH2>
        <div className="space-y-3">
          <Expandable title="Verify the recipient before you sign">The recipient cannot be changed after creation. Confirm it carefully.</Expandable>
          <Expandable title="Use a hardware wallet for large locks">For treasury sized locks, sign from a hardware wallet.</Expandable>
          <Expandable title="Rotate API keys regularly">Revoke and reissue keys on a schedule and after any suspected exposure.</Expandable>
        </div>
      </div>
    ),
  },
  {
    slug: "faq",
    title: "FAQ",
    section: "Resources",
    motif: "interlock",
    eyebrow: "resources",
    description: "Answers to common questions about welock.",
    keywords: ["faq", "questions", "help", "support"],
    headings: [{ id: "general", label: "General" }, { id: "fees", label: "Fees and limits" }],
    Content: () => (
      <div className="space-y-8">
        <Lead>Short answers to the questions we hear most often.</Lead>
        <DocH2 id="general">General</DocH2>
        <div className="space-y-3">
          <Expandable title="Is welock custodial?" defaultOpen>No. welock never holds your keys. Locks are enforced by contracts, not by welock.</Expandable>
          <Expandable title="Which network does welock use?">welock settles on Robinhood Chain, a fully EVM compatible network.</Expandable>
          <Expandable title="Can I cancel a lock early?">No. Locks cannot be shortened. You can only extend the unlock date.</Expandable>
        </div>
        <DocH2 id="fees">Fees and limits</DocH2>
        <div className="space-y-3">
          <Expandable title="What are the fees?">welock charges a small protocol fee per action. Network gas on Robinhood Chain is very low.</Expandable>
          <Expandable title="Is there a recipient limit on multisend?">Multisend handles thousands of recipients per run. Very large lists are split into batches.</Expandable>
        </div>
      </div>
    ),
  },
];

export const SECTIONS = ["Overview", "Products", "Developers", "Resources"];

export const PAGE_MAP = Object.fromEntries(PAGES.map((p) => [p.slug, p]));

export function getNav() {
  return SECTIONS.map((s) => ({ section: s, pages: PAGES.filter((p) => p.section === s) }));
}

export function getPrevNext(slug) {
  const i = PAGES.findIndex((p) => p.slug === slug);
  return { prev: i > 0 ? PAGES[i - 1] : null, next: i < PAGES.length - 1 ? PAGES[i + 1] : null };
}

export const SEARCH_INDEX = PAGES.flatMap((p) => [
  { slug: p.slug, title: p.title, section: p.section, sub: p.description, hay: `${p.title} ${p.description} ${p.keywords.join(" ")}`.toLowerCase() },
  ...p.headings.map((h) => ({ slug: p.slug, hash: h.id, title: h.label, section: p.title, sub: "Section", hay: `${p.title} ${h.label}`.toLowerCase() })),
]);
